import "../../styles/DashBoard_Home.css";
import WarrantySummary from "./Home/WarrantyDeviceSummary";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import rydio from "../../assets/rydio.png";
import { apiFetch } from "../../services/api";
import { getCurrentUserRole, hasPermission, normalizeRole } from "../../utils/rbac";
import ShowSnacker from "./ShowSnacker";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  MdCloudUpload,
  MdMemory,
  MdPerson,
  MdLogout,
  MdRefresh,
  MdDevices,
  MdVerified,
  MdCheckCircle,
  MdFingerprint,
  MdPeopleAlt,
  MdFileDownload,
  MdBarChart,
} from "react-icons/md";

function DashBoardHome() {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(getCurrentUserRole());
  const [chartView, setChartView] = useState<"domains" | "firmware" | "mac">("domains");
  const canAccessDevices = hasPermission("canAccessDevices", userRole);
  const canAccessFirmware = hasPermission("canAccessFirmware", userRole);

  // Device & Activation state
  const [deviceCount, setDeviceCount] = useState<number | undefined>();
  const [allDevices, setAllDevices] = useState<any[]>([]);
  const [todayActivations, setTodayActivations] = useState<number>(0);
  const [monthActivations, setMonthActivations] = useState<number>(0);

  // Firmware state
  const [firmwareList, setFirmwareList] = useState<any[]>([]);
  const [latestFirmware, setLatestFirmware] = useState<string>("—");

  // Admin exclusive states: Master MAC Comparison & Admin Users
  const [macComparison, setMacComparison] = useState<any | null>(null);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);

  // Snackbar notifications
  const [snack, setSnack] = useState({ open: false, message: "", type: "info" });
  const showSnackbar = (message: string, type = "info") => {
    setSnack({ open: true, message, type });
  };

  // Sync role changes
  useEffect(() => {
    const handleRoleChange = (e: any) => {
      const fresh = e?.detail?.role ? normalizeRole(e.detail.role) : getCurrentUserRole();
      setUserRole(fresh);
    };
    window.addEventListener("roleChanged", handleRoleChange);
    return () => window.removeEventListener("roleChanged", handleRoleChange);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("auth");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  // 1. Fetch Device Count, List & Activations
  const fetchDeviceData = useCallback(async () => {
    if (!canAccessDevices) return;
    try {
      const [countRes, allRes, actRes] = await Promise.all([
        apiFetch("/device/count", { method: "GET" }),
        apiFetch("/device/all-devices", { method: "GET" }),
        apiFetch("/device/activationcount", { method: "GET" }),
      ]);

      if (countRes.ok) {
        const data = await countRes.json();
        setDeviceCount(data.count);
      }
      if (allRes.ok) {
        const data = await allRes.json();
        setAllDevices(data.Alldevices || []);
      }
      if (actRes.ok) {
        const data = await actRes.json();
        setTodayActivations(data.todayActivations || 0);
        setMonthActivations(data.monthActivations || 0);
      }
    } catch (err) {
      console.error("Device data fetch error:", err);
    }
  }, [canAccessDevices]);

  // 2. Fetch Firmware Stats
  const fetchFirmwareData = useCallback(async () => {
    if (!canAccessFirmware) return;
    try {
      const res = await apiFetch("/firmware/all", { method: "GET" });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setFirmwareList(data);
          const latest = data.find((f: any) => f.latest) || data[data.length - 1];
          if (latest && latest.version) {
            setLatestFirmware(latest.version);
          }
        }
      }
    } catch (err) {
      console.error("Firmware stats fetch error:", err);
    }
  }, [canAccessFirmware]);

  // 3. Fetch Master MAC Comparison & System Users (Admin only)
  const fetchAdminReportsData = useCallback(async () => {
    if (userRole !== "Administrator" && userRole !== "Super Admin") return;
    try {
      const [macRes, adminRes] = await Promise.all([
        apiFetch("/master-mac/comparison", { method: "GET" }),
        apiFetch("/admin/all", { method: "GET" }),
      ]);

      if (macRes.ok) {
        const data = await macRes.json();
        setMacComparison(data);
      }
      if (adminRes.ok) {
        const data = await adminRes.json();
        setAdminUsers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Admin reports data fetch error:", err);
    }
  }, [userRole]);

  // Initial Load
  useEffect(() => {
    fetchDeviceData();
    fetchFirmwareData();
    fetchAdminReportsData();
  }, [fetchDeviceData, fetchFirmwareData, fetchAdminReportsData]);

  const refreshAllData = () => {
    fetchDeviceData();
    fetchFirmwareData();
    fetchAdminReportsData();
    showSnackbar("Dashboard data refreshed successfully", "info");
  };

  // ==============================================================
  // COMPUTED CONCLUSION METRICS (ADMINISTRATOR OVERVIEW)
  // ==============================================================
  const totalDevices = allDevices.length || deviceCount || 0;
  const approvedCount = allDevices.filter(
    (d) => (d.warrantyApproved || "").toLowerCase() === "approved"
  ).length;
  const expiredCount = allDevices.filter(
    (d) => (d.warrantyApproved || "").toLowerCase() === "expired"
  ).length;
  const pendingWarrantyCount = Math.max(0, totalDevices - (approvedCount + expiredCount));
  const approvedPercent = totalDevices > 0 ? Math.round((approvedCount / totalDevices) * 100) : 0;

  // Master MAC verification metrics
  const macSummary = macComparison?.summary || {
    totalMasterMacs: 0,
    totalDevices: 0,
    matchedCount: 0,
    pendingInMasterCount: 0,
    unverifiedInDeviceCount: 0,
    matchPercentage: "—",
  };
  const matchedCount = macSummary.matchedCount || 0;
  const pendingInMasterCount = macSummary.pendingInMasterCount || 0;
  const unverifiedInDeviceCount = macSummary.unverifiedInDeviceCount || 0;
  const matchPercentage = macSummary.matchPercentage || "—";

  // Firmware fleet adoption metrics
  const devicesOnLatest = allDevices.filter(
    (d) => (d.firmwareversion || d.firmwareVersion) === latestFirmware
  ).length;
  const devicesOnLegacy = Math.max(0, totalDevices - devicesOnLatest);
  const latestFirmwareAdoption =
    totalDevices > 0 ? Math.round((devicesOnLatest / totalDevices) * 100) : 0;

  // User management metrics
  const totalAdmins = adminUsers.length || 0;
  const adminCount = adminUsers.filter(
    (u) => normalizeRole(u.role) === "Administrator"
  ).length;
  const firmwareUserCount = adminUsers.filter(
    (u) => normalizeRole(u.role) === "Firmware Specialist"
  ).length;
  const marketingUserCount = adminUsers.filter(
    (u) => normalizeRole(u.role) === "Marketing"
  ).length;

  // Bar Chart Datasets for Device, Master MAC, and Firmware
  const fwDistributionMap: { [ver: string]: number } = {};
  allDevices.forEach((d) => {
    const rawVer = (d.firmwareversion || d.firmwareVersion || "Legacy").trim();
    const ver = rawVer ? (rawVer.startsWith("v") ? rawVer : `v${rawVer}`) : "Legacy";
    fwDistributionMap[ver] = (fwDistributionMap[ver] || 0) + 1;
  });

  const fwDistributionData = Object.keys(fwDistributionMap).map((ver) => ({
    version: ver,
    devices: fwDistributionMap[ver],
    isLatest:
      ver === latestFirmware ||
      ver === `v${latestFirmware}` ||
      ver.replace(/^v/, "") === latestFirmware.replace(/^v/, ""),
  }));

  if (fwDistributionData.length === 0 && latestFirmware && latestFirmware !== "—") {
    fwDistributionData.push({
      version: latestFirmware.startsWith("v") ? latestFirmware : `v${latestFirmware}`,
      devices: totalDevices,
      isLatest: true,
    });
  }

  const macStatusChartData = [
    {
      name: "Matched (Factory)",
      count: matchedCount,
      fill: "#10b981",
    },
    {
      name: "Unverified (Field)",
      count: unverifiedInDeviceCount,
      fill: "#f59e0b",
    },
    {
      name: "Pending in Master",
      count: pendingInMasterCount,
      fill: "#3b82f6",
    },
  ];

  const domainComparisonData = [
    {
      domain: "Device Fleet",
      Total: totalDevices,
      "Verified / Active": approvedCount,
      "Pending / Attention": pendingWarrantyCount,
    },
    {
      domain: "Master MAC",
      Total: macSummary.totalMasterMacs || totalDevices,
      "Verified / Active": matchedCount,
      "Pending / Attention": unverifiedInDeviceCount,
    },
    {
      domain: "Firmware (OTA)",
      Total: totalDevices,
      "Verified / Active": devicesOnLatest,
      "Pending / Attention": devicesOnLegacy,
    },
  ];

  // Export Executive Report Conclusion to Excel
  const exportReportConclusion = () => {
    try {
      const dateStr = new Date().toISOString().split("T")[0];

      // Sheet 1: Executive Conclusion Summary
      const conclusionRows = [
        {
          "SYSTEM DOMAIN": "Hardware Device Fleet",
          "KEY METRIC": "Total Registered Units",
          "CURRENT PERFORMANCE": `${totalDevices} active units`,
          "BENCHMARK / TARGET": "Fleet Growth",
          "AUDIT STATUS": "ACTIVE",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY": `Total ${totalDevices} connected Rydio units actively syncing in database.`,
        },
        {
          "SYSTEM DOMAIN": "Registration Velocity",
          "KEY METRIC": "Today & Month Activations",
          "CURRENT PERFORMANCE": `+${todayActivations} today | +${monthActivations} this month`,
          "BENCHMARK / TARGET": "Continuous Onboarding",
          "AUDIT STATUS": "ON TRACK",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY": `Healthy customer activation velocity with ${todayActivations} new registrations today.`,
        },
        {
          "SYSTEM DOMAIN": "Factory Master MAC Verification",
          "KEY METRIC": "Compliance Verification Rate",
          "CURRENT PERFORMANCE": `${matchPercentage} (${matchedCount}/${totalDevices} Matched)`,
          "BENCHMARK / TARGET": "100% Factory Compliance",
          "AUDIT STATUS": unverifiedInDeviceCount === 0 ? "OPTIMAL" : "ATTENTION REQUIRED",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY":
            unverifiedInDeviceCount === 0
              ? "All field devices strictly match factory master records. Zero unverified MAC IDs."
              : `${unverifiedInDeviceCount} field device(s) lack factory Master MAC record. Recommend production batch audit.`,
        },
        {
          "SYSTEM DOMAIN": "Factory Master Inventory",
          "KEY METRIC": "Pending Activation in Field",
          "CURRENT PERFORMANCE": `${pendingInMasterCount} factory records`,
          "BENCHMARK / TARGET": "Inventory Buffer",
          "AUDIT STATUS": "IN STOCK / TRANSIT",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY": `${pendingInMasterCount} factory Master MAC records ready and awaiting end-user activation.`,
        },
        {
          "SYSTEM DOMAIN": "Warranty Lifecycle Management",
          "KEY METRIC": "Active Approved Coverage",
          "CURRENT PERFORMANCE": `${approvedCount} units (${approvedPercent}%)`,
          "BENCHMARK / TARGET": ">80% Protected",
          "AUDIT STATUS": "HEALTHY",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY": `${approvedCount} devices covered under active warranty protection.`,
        },
        {
          "SYSTEM DOMAIN": "Warranty Verification Queue",
          "KEY METRIC": "Pending Review",
          "CURRENT PERFORMANCE": `${pendingWarrantyCount} units`,
          "BENCHMARK / TARGET": "Zero Backlog",
          "AUDIT STATUS": pendingWarrantyCount > 0 ? "ACTION REQUIRED" : "CLEAR",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY":
            pendingWarrantyCount > 0
              ? `${pendingWarrantyCount} device warranty registration(s) require review and document verification.`
              : "No pending warranty registrations requiring audit.",
        },
        {
          "SYSTEM DOMAIN": "Firmware Fleet Deployment",
          "KEY METRIC": "Production Release Adoption",
          "CURRENT PERFORMANCE": `v${latestFirmware} (${latestFirmwareAdoption}% of fleet)`,
          "BENCHMARK / TARGET": ">75% Modern Build",
          "AUDIT STATUS": latestFirmwareAdoption >= 75 ? "OPTIMAL" : "ROLLOUT IN PROGRESS",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY": `${devicesOnLatest} of ${totalDevices} field units running latest stable firmware release (v${latestFirmware}).`,
        },
        {
          "SYSTEM DOMAIN": "Legacy Firmware in Field",
          "KEY METRIC": "Eligible for OTA Update",
          "CURRENT PERFORMANCE": `${devicesOnLegacy} units`,
          "BENCHMARK / TARGET": "Minimize Legacy",
          "AUDIT STATUS": devicesOnLegacy > 0 ? "UPDATE CANDIDATES" : "UP TO DATE",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY":
            devicesOnLegacy > 0
              ? `${devicesOnLegacy} devices running earlier builds eligible for OTA firmware rollout.`
              : "Entire fleet is running current certified firmware.",
        },
        {
          "SYSTEM DOMAIN": "Access & Administration",
          "KEY METRIC": "Active Personnel Accounts",
          "CURRENT PERFORMANCE": `${totalAdmins} active users`,
          "BENCHMARK / TARGET": "RBAC Compliance",
          "AUDIT STATUS": "COMPLIANT",
          "EXECUTIVE CONCLUSION & ACTIONABLE TAKEAWAY": `${adminCount} Administrators, ${firmwareUserCount} Firmware Specialists, ${marketingUserCount} Marketing personnel.`,
        },
      ];

      // Sheet 2: Device Fleet Audit
      const deviceRows = allDevices.map((d: any, idx: number) => ({
        "SR NO": idx + 1,
        "MAC ID": d.macId || "—",
        "MODEL NAME": d.modelname || d.modelName || "—",
        "FIRMWARE VERSION": d.firmwareversion || d.firmwareVersion || "—",
        "WARRANTY STATUS": d.warrantyApproved || "Pending",
        "CITY": d.city || "—",
        "REGISTRATION DATE": d.createdAt ? new Date(d.createdAt).toLocaleDateString("en-GB") : "—",
      }));

      const wb = XLSX.utils.book_new();
      const wsConclusion = XLSX.utils.json_to_sheet(conclusionRows);
      XLSX.utils.book_append_sheet(wb, wsConclusion, "Executive_Conclusion");

      if (deviceRows.length > 0) {
        const wsDevices = XLSX.utils.json_to_sheet(deviceRows);
        XLSX.utils.book_append_sheet(wb, wsDevices, "Device_Fleet_Audit");
      }

      XLSX.writeFile(wb, `Studds_Executive_Report_Conclusion_${dateStr}.xlsx`);
      showSnackbar("Executive report conclusion exported to Excel!", "success");
    } catch (err: any) {
      showSnackbar("Error exporting report conclusion", "error");
    }
  };

  return (
    <div className="dashboard-home">
      {/* ============================================================== */}
      {/* UNIFIED TOP HEADER MATCHING MASTER MAC & PROFILE SCREENS       */}
      {/* ============================================================== */}
      <div className="dashboard-header">
        <div className="header-title-section">
          <h2>Dashboard Overview</h2>
          <p>
            {userRole === "Administrator"
              ? "Consolidated fleet analytics, Master MAC verification, and operational report conclusions"
              : `Welcome back! Logged in as ${userRole}`}
          </p>
        </div>

        <div className="dashboard-header-actions">
          {(userRole === "Administrator" || userRole === "Super Admin") && (
            <>
              <button
                type="button"
                className="user-btn-secondary"
                onClick={exportReportConclusion}
                title="Download consolidated executive report in Excel"
              >
                <MdFileDownload size={18} />
                <span>Export Report</span>
              </button>
              <button
                type="button"
                className="add-user-btn"
                onClick={() => navigate("/dashboard/master-mac")}
                title="Open Master MAC Verification"
              >
                <MdCheckCircle size={18} />
                <span>Verify Master MAC</span>
              </button>
            </>
          )}

          <button
            type="button"
            className="user-btn-secondary profile-link-btn"
            onClick={() => navigate("/dashboard/profile")}
          >
            <MdPerson size={18} />
            <span>My Profile</span>
          </button>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span
              className="round-header-icon"
              onClick={refreshAllData}
              title="Refresh Dashboard"
            >
              <MdRefresh size={20} color="#4b5563" />
            </span>
            <span
              className="round-header-icon logout"
              onClick={handleLogout}
              title="Logout"
            >
              <MdLogout size={18} color="#dc2626" />
            </span>
          </div>
        </div>
      </div>

      {/* ============================================================== */}
      {/* ADMINISTRATOR COMMAND CENTER: ALL REPORT CONCLUSION & SUMMARY  */}
      {/* ============================================================== */}
      {(userRole === "Administrator" || userRole === "Super Admin") && (
        <>
          {/* 1. Consolidated KPI Summary Cards Grid */}
          <div className="dashboard-kpi-grid">
            <div className="dash-kpi-card">
              <div className="dash-kpi-icon devices">
                <MdDevices />
              </div>
              <div className="dash-kpi-info">
                <span className="dash-kpi-label">Registered Device</span>
                <span className="dash-kpi-val">{totalDevices}</span>
                <span className="dash-kpi-sub">
                  +{todayActivations} today • {monthActivations} this month
                </span>
              </div>
            </div>

            <div className="dash-kpi-card">
              <div className="dash-kpi-icon mac">
                <MdFingerprint />
              </div>
              <div className="dash-kpi-info">
                <span className="dash-kpi-label">Master MAC Match</span>
                <span className="dash-kpi-val" style={{ color: "#d97706" }}>
                  {matchPercentage}
                </span>
                <span className="dash-kpi-sub">
                  {matchedCount} Matched • {unverifiedInDeviceCount} Unverified
                </span>
              </div>
            </div>

            <div className="dash-kpi-card">
              <div className="dash-kpi-icon warranty">
                <MdVerified />
              </div>
              <div className="dash-kpi-info">
                <span className="dash-kpi-label">Active Warranty</span>
                <span className="dash-kpi-val" style={{ color: "#059669" }}>
                  {approvedCount}{" "}
                  <small style={{ fontSize: "14px", fontWeight: 600 }}>({approvedPercent}%)</small>
                </span>
                <span className="dash-kpi-sub">
                  {pendingWarrantyCount} Pending • {expiredCount} Expired
                </span>
              </div>
            </div>

            <div className="dash-kpi-card">
              <div className="dash-kpi-icon firmware">
                <MdMemory />
              </div>
              <div className="dash-kpi-info">
                <span className="dash-kpi-label">Firmware</span>
                <span className="dash-kpi-val" style={{ color: "#7c3aed" }}>
                  v{latestFirmware}
                </span>
                <span className="dash-kpi-sub">
                  {latestFirmwareAdoption}% Device ({devicesOnLatest} units)
                </span>
              </div>
            </div>

            <div className="dash-kpi-card">
              <div className="dash-kpi-icon users">
                <MdPeopleAlt />
              </div>
              <div className="dash-kpi-info">
                <span className="dash-kpi-label">System Staff</span>
                <span className="dash-kpi-val">{totalAdmins}</span>
                
              </div>
            </div>
          </div>

          {/* 2. Visual Analytics Section: Bar Chart & Donut Chart */}
          <div className="dashboard-charts-grid">
            {/* Interactive Bar Chart for Device, Master MAC, and Firmware */}
            <div className="dashboard-bar-chart-card">
              <div className="chart-card-header">
                <div>
                  <h4>
                    <MdBarChart size={20} color="#e60000" style={{ verticalAlign: "middle", marginRight: "6px" }} />
                    Domain Telemetry & Distribution (Bar Chart)
                  </h4>
                  <p>Comparative metrics across Device fleet, Master MAC verification, and Firmware deployment</p>
                </div>

                {/* View Mode Switcher */}
                <div className="chart-tab-group">
                  <button
                    type="button"
                    className={`chart-tab-btn ${chartView === "domains" ? "active" : ""}`}
                    onClick={() => setChartView("domains")}
                  >
                    All Domains
                  </button>
                  <button
                    type="button"
                    className={`chart-tab-btn ${chartView === "firmware" ? "active" : ""}`}
                    onClick={() => setChartView("firmware")}
                  >
                    Firmware Fleet
                  </button>
                  <button
                    type="button"
                    className={`chart-tab-btn ${chartView === "mac" ? "active" : ""}`}
                    onClick={() => setChartView("mac")}
                  >
                    Master MAC
                  </button>
                </div>
              </div>

              {/* Responsive Bar Chart */}
              <div className="bar-chart-container">
                {chartView === "domains" && (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={domainComparisonData} margin={{ top: 15, right: 15, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="domain" tick={{ fill: "#475569", fontSize: 12, fontWeight: 600 }} />
                      <YAxis tick={{ fill: "#64748b", fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          color: "#fff",
                          borderRadius: "8px",
                          border: "none",
                          fontSize: "12px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                        }}
                      />
                      <Legend wrapperStyle={{ paddingTop: "8px", fontSize: "12px" }} />
                      <Bar dataKey="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Total Fleet Units" />
                      <Bar dataKey="Verified / Active" fill="#10b981" radius={[4, 4, 0, 0]} name="Verified / Active / Latest" />
                      <Bar dataKey="Pending / Attention" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Pending / Unverified / Legacy" />
                    </BarChart>
                  </ResponsiveContainer>
                )}

                {chartView === "firmware" && (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={fwDistributionData} margin={{ top: 15, right: 20, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="version" tick={{ fill: "#475569", fontSize: 12, fontWeight: 600 }} />
                      <YAxis tick={{ fill: "#64748b", fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          color: "#fff",
                          borderRadius: "8px",
                          border: "none",
                          fontSize: "12px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                        }}
                      />
                      <Legend wrapperStyle={{ paddingTop: "8px", fontSize: "12px" }} />
                      <Bar dataKey="devices" fill="#7c3aed" radius={[4, 4, 0, 0]} name="Connected Devices" />
                    </BarChart>
                  </ResponsiveContainer>
                )}

                {chartView === "mac" && (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={macStatusChartData} margin={{ top: 15, right: 20, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 12, fontWeight: 600 }} />
                      <YAxis tick={{ fill: "#64748b", fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          color: "#fff",
                          borderRadius: "8px",
                          border: "none",
                          fontSize: "12px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                        }}
                      />
                      <Legend wrapperStyle={{ paddingTop: "8px", fontSize: "12px" }} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} name="MAC ID Count">
                        {macStatusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Donut Card for Warranty */}
            <div className="dashboard-donut-card">
              <WarrantySummary />
            </div>
          </div>


        </>
      )}

      {/* ============================================================== */}
      {/* FIRMWARE SPECIALIST DASHBOARD OVERVIEW                         */}
      {/* ============================================================== */}
      {userRole === "Firmware Specialist" && (
        <div style={{ marginTop: "15px" }}>
          <h1 className="title-reg">Firmware Management Overview</h1>
          <div
            className="dashboard-analysis"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}
          >
            <div
              className="box-devices"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <div className="rydio-info">
                <h3>TOTAL RELEASES</h3>
                <span className="cnt-dvc">{firmwareList.length}</span>
                <span className="reg-dvc">Available Firmware Versions</span>
              </div>
            </div>
            <div
              className="box-devices"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <div className="rydio-info">
                <h3>LATEST VERSION</h3>
                <span className="cnt-dvc" style={{ fontSize: "28px" }}>
                  {latestFirmware}
                </span>
                <span className="reg-dvc">Active Production Release</span>
              </div>
            </div>
            <div
              className="box-devices"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "10px",
                background: "#ffffff",
                border: "1px solid #e2e8f0",
              }}
            >
              <button
                type="button"
                className="user-btn-primary"
                onClick={() => navigate("/dashboard/firmwareupdate")}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <MdCloudUpload size={18} /> Upload Firmware
              </button>
              <button
                type="button"
                className="user-btn-secondary"
                onClick={() => navigate("/dashboard/firmwares")}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <MdMemory size={18} /> View All Firmwares
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* MARKETING SPECIALIST DASHBOARD OVERVIEW                        */}
      {/* ============================================================== */}
      {userRole === "Marketing" && (
        <>
          <h1 className="title-reg">Total Registered devices</h1>
          <div className="dashboard-analysis">
            <div className="box-devices">
              <div className="rydio-info">
                <h3> RYDIO DEVICES</h3>
                <span className="cnt-dvc">{deviceCount !== undefined ? deviceCount : "—"}</span>
                <span className="reg-dvc">Total registered devices</span>
              </div>
              <div className="rydio-img">
                <img src={rydio} alt="rydio" />
              </div>
            </div>
          </div>

          <div className="dashboard-warranty-analysis">
            <div className="warranty-summary">
              <WarrantySummary />
            </div>
          </div>
        </>
      )}

      {/* Feedback Toast */}
      <ShowSnacker
        open={snack.open}
        message={snack.message}
        type={snack.type as any}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}

export default DashBoardHome;